import { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'
import { LOT_ACQUISITION_TEMPLATE, SPEC_BUILD_TEMPLATE, CUSTOM_HOME_TEMPLATE } from '../constants'

function mapTemplate(t) {
  return {
    id: t.id, name: t.name, projectType: t.project_type,
    description: t.description || "", tasks: t.tasks || [],
    isDefault: t.is_default, createdAt: t.created_at, updatedAt: t.updated_at,
  }
}

export function useTemplates(session, showToast) {
  const [savedTemplates, setSavedTemplates] = useState([])
  const [editingTemplateId, setEditingTemplateId] = useState(null)
  const [newTemplateType, setNewTemplateType] = useState("construction")
  const [templateFromProject, setTemplateFromProject] = useState(null)

  useEffect(() => {
    if (!session) return
    let cancelled = false
    async function loadTemplates() {
      const { data, error } = await supabase.from("templates").select("*").order("created_at")
      if (cancelled) return
      if (error) { console.error("Failed to load templates:", error); return }
      if (data && data.length > 0) {
        setSavedTemplates(data.map(mapTemplate))
      } else {
        const { data: { user } } = await supabase.auth.getUser()
        if (cancelled || !user) return
        // Re-check after async gap to prevent race condition in React Strict Mode
        const { data: recheck } = await supabase.from("templates").select("id").limit(1)
        if (cancelled || (recheck && recheck.length > 0)) {
          if (recheck && recheck.length > 0) {
            const { data: fresh } = await supabase.from("templates").select("*").order("created_at")
            if (!cancelled && fresh) setSavedTemplates(fresh.map(mapTemplate))
          }
          return
        }
        const mapTasks = (tmpl) => tmpl.map(t => ({ refId: t.refId, name: t.name, durationDays: null, predecessors: [], estimatedCost: null, parentRefId: t.parentRefId || null, isPhase: t.isPhase || false }));
        const seedRows = [
          {
            user_id: user.id, name: "Lot Acquisition - Standard", project_type: "land",
            description: "Default lot acquisition template with due diligence, closing, and entitlement phases", is_default: true,
            tasks: mapTasks(LOT_ACQUISITION_TEMPLATE),
          },
          {
            user_id: user.id, name: "Residential Spec Build - Standard", project_type: "construction",
            description: "Default spec build template — streamlined 10-phase residential construction", is_default: true,
            tasks: mapTasks(SPEC_BUILD_TEMPLATE),
          },
          {
            user_id: user.id, name: "Custom Home Build - Standard", project_type: "construction",
            description: "Full custom home template — 10 phases with specialty systems and owner walk-throughs", is_default: false,
            tasks: mapTasks(CUSTOM_HOME_TEMPLATE),
          },
        ]
        const { data: seeded, error: seedErr } = await supabase.from("templates").insert(seedRows).select()
        if (cancelled) return
        if (seedErr) { console.error("Failed to seed templates:", seedErr); return }
        if (seeded) setSavedTemplates(seeded.map(mapTemplate))
      }
    }
    loadTemplates()
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- stabilize: only re-fetch on user change, not token refresh
  }, [session?.user?.id])

  async function saveTemplateToSupabase(template) {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return null
    const row = {
      user_id: user.id,
      name: template.name,
      project_type: template.projectType,
      description: template.description || null,
      tasks: template.tasks || [],
      is_default: template.isDefault || false,
      updated_at: new Date().toISOString(),
    }
    if (template.id) {
      const { error } = await supabase.from("templates").update(row).eq("id", template.id)
      if (error) { console.error("Failed to update template:", error); showToast("Failed to save template.", "error"); return null }
      setSavedTemplates(ts => ts.map(t => t.id === template.id ? { ...t, ...template, updatedAt: row.updated_at } : t))
      return template.id
    } else {
      const { data, error } = await supabase.from("templates").insert(row).select().single()
      if (error) { console.error("Failed to create template:", error); showToast("Failed to create template.", "error"); return null }
      setSavedTemplates(ts => [...ts, mapTemplate(data)])
      return data.id
    }
  }

  async function deleteTemplateFromSupabase(templateId) {
    const { error } = await supabase.from("templates").delete().eq("id", templateId)
    if (error) { console.error("Failed to delete template:", error); showToast("Failed to delete template.", "error"); return }
    setSavedTemplates(ts => ts.filter(t => t.id !== templateId))
  }

  async function duplicateTemplate(templateId) {
    const orig = savedTemplates.find(t => t.id === templateId)
    if (!orig) return
    await saveTemplateToSupabase({ name: orig.name + " (Copy)", projectType: orig.projectType, description: orig.description, tasks: orig.tasks, isDefault: false })
  }

  async function setTemplateDefault(templateId) {
    const template = savedTemplates.find(t => t.id === templateId)
    if (!template) return
    const others = savedTemplates.filter(t => t.projectType === template.projectType && t.id !== templateId && t.isDefault)
    for (const o of others) {
      await supabase.from("templates").update({ is_default: false }).eq("id", o.id)
    }
    await supabase.from("templates").update({ is_default: true }).eq("id", templateId)
    setSavedTemplates(ts => ts.map(t => {
      if (t.projectType === template.projectType) return { ...t, isDefault: t.id === templateId }
      return t
    }))
  }

  async function saveProjectAsTemplate(project) {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return null
    const tasks = (project.tasks || []).map((t, i) => ({
      refId: `t_${i}`,
      name: t.name,
      durationDays: t.durationDays ?? null,
      predecessors: [],
      estimatedCost: t.estimatedCost ?? null,
    }))
    const idToRef = new Map();
    (project.tasks || []).forEach((t, i) => idToRef.set(t.supabaseId, `t_${i}`));
    for (let i = 0; i < (project.tasks || []).length; i++) {
      const preds = project.tasks[i].predecessors || [];
      tasks[i].predecessors = preds.map(pid => idToRef.get(pid)).filter(Boolean);
    }
    return { name: project.name, projectType: project.type, description: "", tasks, isDefault: false }
  }

  return {
    savedTemplates,
    editingTemplateId, setEditingTemplateId,
    newTemplateType, setNewTemplateType,
    templateFromProject, setTemplateFromProject,
    saveTemplateToSupabase,
    deleteTemplateFromSupabase,
    duplicateTemplate,
    setTemplateDefault,
    saveProjectAsTemplate,
  }
}
